import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { useNotificationsStore } from "../stores/notificationsStore";
import {
  notificationRoute,
  notificationSubtitle,
  notificationTitle,
} from "./notification-formatters";
import { notificationIcon, notificationIconTint } from "./notification-icons";
import { ConnectionStatusDot } from "./ConnectionStatusDot";
import { formatRelative } from "@/lib/format/date";
import { cn } from "@/lib/utils/cn";
import type { StoredNotification } from "../types";

const VISIBLE_COUNT = 10;

export function NotificationsPanel({ onSeeAll }: { onSeeAll?: () => void }) {
  const notifications = useNotificationsStore((s) => s.notifications);
  const unread = useNotificationsStore((s) => s.unreadCount);
  const markAsRead = useNotificationsStore((s) => s.markAsRead);
  const markAllAsRead = useNotificationsStore((s) => s.markAllAsRead);
  const status = useNotificationsStore((s) => s.connectionStatus);
  const navigate = useNavigate();

  const visible = notifications.slice(0, VISIBLE_COUNT);

  function handleClick(n: StoredNotification) {
    markAsRead(n.id);
    navigate(notificationRoute(n));
  }

  return (
    <div className="flex flex-col">
      <header className="flex items-center justify-between border-b border-border-soft px-3 py-2.5">
        <div>
          <h2 className="text-[13px] font-semibold">Notificações</h2>
          <p className="flex items-center gap-1.5 text-[11px] text-ink-soft">
            <ConnectionStatusDot />
            {status === "connected" && "Tempo real conectado"}
            {status === "reconnecting" && "Reconectando…"}
            {status === "disconnected" && "Desconectado"}
          </p>
        </div>
        {unread > 0 && (
          <button
            type="button"
            onClick={markAllAsRead}
            className="text-[11px] font-medium text-brand-600 hover:text-brand-700"
          >
            Marcar todas como lidas
          </button>
        )}
      </header>

      {visible.length === 0 ? (
        <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-bg text-ink-muted">
            <Bell size={18} />
          </span>
          <p className="text-[12px] font-semibold">Você está em dia</p>
          <p className="text-[11px] text-ink-soft">
            Avisaremos por aqui quando algo novo chegar.
          </p>
        </div>
      ) : (
        <ul className="max-h-[420px] overflow-y-auto">
          {visible.map((n) => (
            <li key={n.id}>
              <button
                type="button"
                onClick={() => handleClick(n)}
                className={cn(
                  "flex w-full items-start gap-3 border-b border-border-soft px-3 py-2.5 text-left transition-colors hover:bg-bg/60 last:border-b-0",
                  !n.read && "bg-brand-50/30",
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                    notificationIconTint[n.type],
                  )}
                >
                  {notificationIcon(n.type)}
                </span>
                <div className="min-w-0 flex-1">
                  <p
                    className={cn(
                      "truncate text-[12.5px]",
                      n.read ? "text-ink-muted" : "font-semibold text-ink",
                    )}
                  >
                    {notificationTitle(n)}
                  </p>
                  {notificationSubtitle(n) && (
                    <p className="truncate text-[11px] text-ink-soft">{notificationSubtitle(n)}</p>
                  )}
                  <p className="text-[10.5px] text-ink-soft">{formatRelative(n.receivedAt)}</p>
                </div>
                {!n.read && (
                  <span aria-hidden className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-500" />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      <footer className="border-t border-border-soft px-3 py-2">
        <button
          type="button"
          onClick={onSeeAll}
          className="w-full rounded-lg py-1.5 text-center text-[12px] font-medium text-brand-600 hover:bg-bg/60 hover:text-brand-700"
        >
          Ver todas
        </button>
      </footer>
    </div>
  );
}
